import React from "react";
import {Alert, Dialog, DialogContent, IconButton, Skeleton, Tab, Tooltip} from "@mui/material";
import {CustomTabs} from "~/components/tabs/Tabs.tsx";
import {
    CloseOutlined,
    DownloadOutlined,
    EditOutlined,
    KeyboardArrowDownOutlined,
    KeyboardArrowUpOutlined,
} from "@mui/icons-material";
import {Tag} from "~/components/tag/Tag.tsx";
import {LIFECYCLE_COLOR, LIFECYCLE_LABEL, typeLabel} from "../contractExplorerTypes.ts";
import {useContractDetailQuery} from "../useContractDetailQuery.ts";
import {ContractDetailContent} from "./ContractDetailContent.tsx";
import {ContractDetailEdit} from "./ContractDetailEdit.tsx";
import {ContractLinksPanel} from "./ContractLinksPanel.tsx";
import {ContractInsightsPanel} from "./ContractInsightsPanel.tsx";

type Props = {
    bagId: number;
    contractId: number;
    onClose: () => void;
    onOpenDocument: (contractId: number) => void;
    /** Owned by the page (it must suspend ↑/↓ stepping while a draft is open,
     *  and a contract switch naturally leaves edit mode). */
    editing: boolean;
    onEditingChange: (editing: boolean) => void;
    /** A review was saved: the row's title/amount in the grid are now stale. */
    onSaved: () => void;
    /** Open a different contract in this modal (competing-contract links). */
    onOpenContract: (contractId: number) => void;
    /** Contract-type labels the review form offers, most relevant first. */
    typeSuggestions: string[];
    /** Step to the previous/next row of the grid; undefined at the ends. */
    onPrevious?: () => void;
    onNext?: () => void;
};

/**
 * The contract detail at full width, over the list.
 *
 * A modal rather than its own route: the grid stays mounted underneath, so
 * closing is instant and the list keeps its scroll position, loaded chunks and
 * filters. Prev/next and ↑/↓ keep working while it is open.
 */
export const ContractDetailModal: React.FC<Props> = (
    {bagId, contractId, onClose, onOpenDocument, editing, onEditingChange, onSaved,
        onOpenContract, typeSuggestions, onPrevious, onNext},
) => {
    const {data, isPending, isError, error} = useContractDetailQuery(bagId, contractId);
    // The tab survives contract switching on purpose: reviewing matches is a
    // walk across contracts, so ↑/↓ should keep you on the Transactions tab.
    const [tab, setTab] = React.useState<'details' | 'links' | 'insights'>('details');

    return (
        <Dialog
            open
            // While a draft is open, Esc/backdrop do nothing: leaving edit mode
            // is explicit (Cancel or Save), so a stray key cannot drop edits.
            onClose={editing ? undefined : onClose}
            // Fixed viewport-relative size (not `maxWidth`), so the dialog does
            // not resize as you step between contracts with different amounts of
            // content — only its body scrolls.
            maxWidth={false}
            className="ce-detail-modal"
            slotProps={{paper: {sx: {
                width: '92vw',
                maxWidth: 1400,
                height: '88vh',
                maxHeight: '88vh',
            }}}}>
            <header className="ce-modal-head">
                <div className="ce-modal-title">
                    <span className="ce-modal-name">
                        {isPending ? <Skeleton width={280}/> : (data?.contract_title || data?.file_name || 'Contract')}
                    </span>
                    {data && (
                        <span className="ce-modal-chips">
                            <Tag label={LIFECYCLE_LABEL[data.lifecycle]} color={LIFECYCLE_COLOR[data.lifecycle]}/>
                            <Tag label={typeLabel(data.contract_type_clean)} color="default"/>
                            {data.has_review && <Tag label={`Edited · v${data.version_no}`} color="info"/>}
                        </span>
                    )}
                </div>

                <div className="ce-modal-actions">
                    <Tooltip title="Previous contract (↑)">
                        <IconButton size="small" onClick={onPrevious} disabled={!onPrevious || editing}>
                            <KeyboardArrowUpOutlined fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Next contract (↓)">
                        <IconButton size="small" onClick={onNext} disabled={!onNext || editing}>
                            <KeyboardArrowDownOutlined fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    {data && !editing && tab === 'details' && (
                        <Tooltip title="Edit (adds a review version and freezes re-parses)">
                            <IconButton size="small" onClick={() => onEditingChange(true)}>
                                <EditOutlined fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    {data?.has_file && (
                        <Tooltip title="Contract document">
                            <IconButton size="small" onClick={() => onOpenDocument(contractId)}>
                                <DownloadOutlined fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={editing ? 'Cancel the edit first' : 'Close (Esc)'}>
                        <span>
                            <IconButton size="small" onClick={onClose} disabled={editing}>
                                <CloseOutlined fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            </header>

            {/* While a draft is open the tabs hide: leaving the form is explicit
                (Cancel/Save), so a tab click cannot silently drop edits. */}
            {!editing && (
                <CustomTabs value={tab} onChange={(_e, value) => setTab(value)} className="ce-modal-tabs">
                    <Tab label="Details" value="details"/>
                    <Tab label="Spend under contract" value="links"/>
                    <Tab label="Insights" value="insights"/>
                </CustomTabs>
            )}

            <DialogContent dividers sx={{flex: 1, overflowY: 'auto'}}>
                {isError && <Alert severity="error">Could not load this contract: {error?.message}</Alert>}
                {isPending && <Skeleton variant="rectangular" height={320}/>}
                {data && !editing && tab === 'details' && <ContractDetailContent data={data}/>}
                {data && !editing && tab === 'links' &&
                    <ContractLinksPanel bagId={bagId} contractId={contractId} currency={data.currency}
                                        versionNo={data.version_no} effectiveDate={data.effective_date}
                                        onOpenContract={onOpenContract}/>}
                {data && !editing && tab === 'insights' &&
                    <ContractInsightsPanel bagId={bagId} contractId={contractId} currency={data.currency}/>}
                {data && editing &&
                    <ContractDetailEdit
                        bagId={bagId}
                        data={data}
                        typeSuggestions={typeSuggestions}
                        onDone={saved => {
                            onEditingChange(false);
                            if (saved) onSaved();
                        }}
                    />}
            </DialogContent>
        </Dialog>
    );
};
